import { Modifier } from '@dnd-kit/core';

type OverlaySize = { width: number; height: number };

const SIDEBAR_DRAG_OVERLAY_DIMENSIONS: Record<string, OverlaySize> = {
  'new-space': { width: 100, height: 60 },
  'new-condenser': { width: 40, height: 40 },
  'new-condenser-ground': { width: 40, height: 40 },
  'new-condenser-wall': { width: 40, height: 40 },
  'new-condenser-roof': { width: 40, height: 40 },
  'new-air-handler': { width: 40, height: 40 },
  'new-air-handler-high-wall': { width: 40, height: 40 },
  'new-air-handler-low-wall': { width: 40, height: 40 },
  'new-air-handler-floor-ceiling': { width: 40, height: 40 },
  'new-air-handler-ceiling-cassette': { width: 40, height: 40 },
  'new-air-handler-ceiling-cassette-slim-fit': { width: 40, height: 40 },
  'new-air-handler-concealed-duct': { width: 40, height: 40 },
  'new-air-handler-ducted': { width: 40, height: 40 },
  'new-window-small': { width: 40, height: 40 },
  'new-window-medium': { width: 40, height: 40 },
  'new-window-large': { width: 40, height: 40 },
  'new-door-wood': { width: 40, height: 40 },
  'new-door-metal': { width: 40, height: 40 },
  'new-door-pet': { width: 40, height: 40 },
  'new-door-glass-single': { width: 40, height: 40 },
  'new-door-glass-double': { width: 40, height: 40 },
  'new-door-garage-single': { width: 40, height: 40 },
  'new-door-garage-double': { width: 40, height: 40 },
  'new-lineset': { width: 40, height: 40 },
  'new-appliance': { width: 40, height: 40 },
  'new-skylight-small': { width: 30, height: 30 },
  'new-skylight-medium': { width: 40, height: 40 },
  'new-skylight-large': { width: 50, height: 50 },
  'place-existing-equipment': { width: 30, height: 30 },
  'place-electrical-panel': { width: 30, height: 30 }
};

type ClientPoint = { x: number; y: number };

function getActivatorClientPoint(
  activatorEvent: Event
): ClientPoint | undefined {
  if ('touches' in activatorEvent) {
    const touch = (activatorEvent as TouchEvent).touches[0];
    if (!touch) {
      return undefined;
    }
    return { x: touch.clientX, y: touch.clientY };
  }
  if ('clientX' in activatorEvent) {
    const mouseEvent = activatorEvent as MouseEvent;
    return { x: mouseEvent.clientX, y: mouseEvent.clientY };
  }
  return undefined;
}

export function getCenterOnCursorAdjustment({
  itemType,
  activeNodeRect,
  activatorEvent,
  zoom
}: {
  itemType: string | undefined;
  activeNodeRect: { left: number; top: number };
  activatorEvent: Event;
  zoom: number;
}): ClientPoint | undefined {
  if (!itemType) {
    return undefined;
  }
  const dimensions = SIDEBAR_DRAG_OVERLAY_DIMENSIONS[itemType];
  if (!dimensions) {
    return undefined;
  }
  const point = getActivatorClientPoint(activatorEvent);
  if (!point) {
    return undefined;
  }
  return {
    x: point.x - activeNodeRect.left - (dimensions.width * zoom) / 2,
    y: point.y - activeNodeRect.top - (dimensions.height * zoom) / 2
  };
}

export function createCenterOnCursorModifier(zoom = 1): Modifier {
  return ({ transform, activeNodeRect, activatorEvent, active }) => {
    if (!activeNodeRect || !activatorEvent) {
      return transform;
    }
    const adjustment = getCenterOnCursorAdjustment({
      itemType: active?.data?.current?.type as string | undefined,
      activeNodeRect,
      activatorEvent,
      zoom
    });
    if (!adjustment) {
      return transform;
    }
    return {
      ...transform,
      x: transform.x + adjustment.x,
      y: transform.y + adjustment.y
    };
  };
}
